// localSavings — the two non-negotiable rules for any "cheaper N miles away"
// claim on the site (2026-09-15). Mirrors the gate ScenarioCard's local-rank
// badge applies (src/components/ScenarioCard.tsx, "PLAUSIBILITY GATE") so the
// hospital page's city view and scenario-distance tool say exactly what the
// card says. Change both together, or better, point the card here.
//
// 1. NEVER print a dollar saving without the plausibility gate. Scenario totals
//    have real defects at both extremes — $600 hip replacements from
//    chargemaster mapping errors, and nine-figure drug scenarios (the worst
//    row in scenario_hospital_coverage is $3.2B). Ratio tests alone don't catch
//    the high end because the national side is inflated too, hence absolute
//    bounds. Ungated, the badge fired on 21.6M of 24.1M rows — noise.
//    COUNTS and RANKS are not gated: they depend on ordering, not magnitude.
//
// 2. A LONGER DRIVE MUST EARN IT. $500 and 20% to show a saving at all,
//    rising to $2,000 past 120 miles. "Drive 250 miles to save $600" is
//    advice nobody should act on. Do not lower these.

// Loosened 2026-09-26 (Trent: the gate hid "almost all scenarios shown on
// hospitals"). Measured on a 2% sample of scenario_hospital_coverage (568K rows
// at ≥75% coverage): the old $200K ceiling + 7× bound refused 10.2% of rows and
// 40.4% of the rows where the hospital is in its scenario's priciest 5% — the
// very rows a reader needs. 53K of those were real inpatient totals between
// $200K and $2M; only 813 were refused for sitting 7–15× national. At $2M and
// 15×: 0.8% refused overall, 2.5% of the priciest-5%. The defect tail the gate
// exists for (4.5K sampled rows over $2M, up to the $3.2B unit-quantity
// blowups) is still out. Texas Scottish Rite's priciest-5% scenarios: 85% → 8%.
export const PRICE_FLOOR = 200;
export const PRICE_CEILING = 2_000_000;
export const BEST_MIN_SHARE_OF_NATIONAL = 0.40;   // a candidate price must be ≥ 40% of national
export const HERE_MAX_MULTIPLE_OF_NATIONAL = 15;   // this hospital's price must be ≤ 15× national
export const SAVING_FLOOR_NEAR = 500;              // ≤ 120 miles
export const SAVING_FLOOR_FAR = 2000;              // > 120 miles
export const SAVING_MIN_SHARE = 0.20;              // and ≥ 20% of the price here
export const FAR_MILES = 120;

/** Is a candidate (alternative hospital) price plausible enough to quote in dollars? */
export function candidatePlausible(price: number | null | undefined, national: number | null | undefined): boolean {
  return price != null && national != null && national > 0
    && price >= PRICE_FLOOR && price <= PRICE_CEILING && price >= national * BEST_MIN_SHARE_OF_NATIONAL;
}

/** Is this hospital's own price plausible enough to compute a saving against? */
export function herePlausible(price: number | null | undefined, national: number | null | undefined): boolean {
  return price != null && national != null && national > 0
    && price >= PRICE_FLOOR && price <= PRICE_CEILING && price <= national * HERE_MAX_MULTIPLE_OF_NATIONAL;
}

export function savingFloor(miles: number): number {
  return miles > FAR_MILES ? SAVING_FLOOR_FAR : SAVING_FLOOR_NEAR;
}

export type DriveVerdict =
  | { kind: "worth_it"; saving: number; share: number }          // gated dollar saving, floors cleared
  | { kind: "marginal"; share: number | null }                     // cheaper, but not by enough to justify the drive (no $ figure)
  | { kind: "not_cheaper" }                                        // same price or more expensive
  | { kind: "no_baseline" }                                        // THIS hospital's price fails the gate — the alternative's price is fine, but no saving can be claimed
  | { kind: "unquotable" };                                        // the alternative's price fails the plausibility gate — never print it as a saving

/**
 * The trade-off verdict for one alternative. Returns a dollar saving ONLY when
 * both prices pass the gate and the drive is earned; everything else carries
 * no dollar figure.
 */
export function driveVerdict(here: number | null | undefined, candidate: number | null | undefined, national: number | null | undefined, miles: number): DriveVerdict {
  if (candidate == null || !candidatePlausible(candidate, national)) return { kind: "unquotable" };
  if (here == null || !herePlausible(here, national)) return { kind: "no_baseline" };
  if (candidate >= here) return { kind: "not_cheaper" };
  const saving = here - candidate;
  const share = saving / here;
  if (saving >= savingFloor(miles) && share >= SAVING_MIN_SHARE) return { kind: "worth_it", saving, share };
  return { kind: "marginal", share };
}

/**
 * Distance bands, matching hospital_neighbors / scenario_hospital_coverage:
 * a band is an EXCLUSIVE upper bound bucket (30 = 0–30, 60 = >30–60, …), so
 * "within N miles" is always band <= N — never band == N.
 */
export const DISTANCE_BANDS = [30, 60, 120, 300] as const;
export function bandOf(miles: number): (typeof DISTANCE_BANDS)[number] | null {
  for (const b of DISTANCE_BANDS) if (miles <= b) return b;
  return null;
}
